import { useState, useRef, useCallback } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  LinearProgress,
  Link,
} from "@mui/material"
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
} from "@mui/icons-material"
import { importService, ImportPreviewResponse, ImportConfirmResponse, ImportRow } from "../../services/import/importService"
import { showSuccessToast, showErrorToast } from "../../utils/errorHandler"
import { useQueryClient } from "@tanstack/react-query"

type Entity = "customers" | "skus"

interface BulkImportDialogProps {
  open: boolean
  onClose: () => void
  entity: Entity
}

const STEPS = ["Upload", "Preview", "Done"]

const CUSTOMER_TEMPLATE = `name,customer_code,booker_name,area_name,phone,cnic,address,discount,is_filer
Ahmed Traders,,Ali Khan,Gulshan,03001234567,,Shop 5 Main Bazar,3,no
Karachi Mart,,Sara Ahmed,Clifton,,42201-1234567-1,Tariq Road,0,no
`

const SKU_TEMPLATE = `name,packing,standard_rate,special_rate,sort_order
Sunsilk Shampoo 200ml,12,450,420,10
Surf Excel 1kg,6,850,,20
`

function downloadTemplate(entity: Entity) {
  const content = entity === "customers" ? CUSTOMER_TEMPLATE : SKU_TEMPLATE
  const filename = entity === "customers" ? "customers_template.csv" : "skus_template.csv"
  const blob = new Blob([content], { type: "text/csv" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function ActionChip({ action }: { action: ImportRow["action"] }) {
  const map: Record<string, { label: string; color: "success" | "warning" | "error" | "info" }> = {
    create: { label: "Create", color: "success" },
    update: { label: "Update", color: "info" },
    error: { label: "Error", color: "error" },
    warning: { label: "Warning", color: "warning" },
  }
  const { label, color } = map[action] ?? { label: action, color: "info" }
  return <Chip label={label} color={color} size="small" />
}

function SummaryBar({ summary }: { summary: ImportPreviewResponse["summary"] }) {
  return (
    <Box display="flex" gap={1.5} flexWrap="wrap" mb={2}>
      <Chip label={`${summary.total} total`} variant="outlined" size="small" />
      <Chip label={`${summary.toCreate} to create`} color="success" size="small" />
      <Chip label={`${summary.toUpdate} to update`} color="info" size="small" />
      {summary.errors > 0 && (
        <Chip label={`${summary.errors} errors`} color="error" size="small" />
      )}
    </Box>
  )
}

export default function BulkImportDialog({ open, onClose, entity }: BulkImportDialogProps) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState(0)
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null)
  const [result, setResult] = useState<ImportConfirmResponse | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const reset = useCallback(() => {
    setStep(0)
    setFile(null)
    setPreview(null)
    setResult(null)
    setErrorMsg(null)
    setPreviewing(false)
    setConfirming(false)
  }, [])

  const handleClose = () => {
    reset()
    onClose()
  }

  const acceptFile = (f: File | null) => {
    if (!f) return
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setErrorMsg("Only CSV files are supported.")
      return
    }
    setFile(f)
    setErrorMsg(null)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0] ?? null)
    e.target.value = ""
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    acceptFile(e.dataTransfer.files?.[0] ?? null)
  }

  const canPreview = !!file

  const handlePreview = async () => {
    if (!file) return
    setPreviewing(true)
    setErrorMsg(null)
    try {
      let data: ImportPreviewResponse
      if (entity === "customers") {
        data = await importService.previewCustomers(file)
      } else {
        data = await importService.previewSkus(file)
      }
      setPreview(data)
      setStep(1)
    } catch (err: any) {
      setErrorMsg(err?.message ?? "Preview failed. Please try again.")
    } finally {
      setPreviewing(false)
    }
  }

  const handleConfirm = async () => {
    if (!file || !preview) return
    setConfirming(true)
    setErrorMsg(null)
    try {
      let data: ImportConfirmResponse
      if (entity === "customers") {
        data = await importService.confirmCustomers(file)
      } else {
        data = await importService.confirmSkus(file)
      }
      setResult(data)
      setStep(2)
      queryClient.invalidateQueries({ queryKey: [entity === "customers" ? "customers" : "skus"] })
      showSuccessToast(data.message)
    } catch (err: any) {
      setErrorMsg(err?.message ?? "Import failed. Please try again.")
      showErrorToast({ message: err?.message ?? "Import failed" })
    } finally {
      setConfirming(false)
    }
  }

  const validRows = preview?.rows.filter((r) => r.action === "create" || r.action === "update") ?? []
  const hasErrors = (preview?.summary.errors ?? 0) > 0

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        Import {entity === "customers" ? "Customers" : "SKUs"} from CSV
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={step} sx={{ mt: 2, mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* ── Step 0: Upload ─────────────────────────────────── */}
        {step === 0 && (
          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="body2" color="text.secondary">
                Download the template, fill it in, then upload it below.
              </Typography>
              <Button
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => downloadTemplate(entity)}
              >
                Download Template
              </Button>
            </Box>

            {entity === "customers" && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Both <strong>booker_name</strong> and <strong>area_name</strong> columns are required. Values must exactly match the names of bookers and areas already created in the dashboard. Use <strong>customer_code</strong> when updating a customer whose name is shared by multiple shops; leave it empty for new customers.
              </Alert>
            )}

            {/* Drop zone */}
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: 2,
                borderStyle: "dashed",
                borderColor: dragOver ? "primary.main" : "grey.400",
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragOver ? "action.hover" : "background.default",
                transition: "all 0.2s",
                "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
              }}
            >
              <UploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
              <Typography variant="body1" fontWeight={500}>
                {file ? file.name : "Drag & drop your CSV here, or click to browse"}
              </Typography>
              {file && (
                <Typography variant="caption" color="text.secondary">
                  {(file.size / 1024).toFixed(1)} KB
                </Typography>
              )}
              {!file && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  CSV only · Max 2 MB · Max 1,000 rows
                </Typography>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </Box>

            {file && (
              <Box mt={1} display="flex" justifyContent="flex-end">
                <Link
                  component="button"
                  variant="caption"
                  color="text.secondary"
                  onClick={(e) => { e.stopPropagation(); setFile(null) }}
                >
                  Remove file
                </Link>
              </Box>
            )}

            {errorMsg && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMsg}
              </Alert>
            )}

            {previewing && <LinearProgress sx={{ mt: 2 }} />}
          </Box>
        )}

        {/* ── Step 1: Preview ────────────────────────────────── */}
        {step === 1 && preview && (
          <Box>
            <SummaryBar summary={preview.summary} />

            {hasErrors && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {preview.summary.errors} row(s) have errors and will be skipped during import.
              </Alert>
            )}

            {validRows.length === 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                No valid rows to import. Please fix the errors in your CSV and try again.
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 64 }}>Row</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 100 }}>Action</TableCell>
                    {entity === "customers" ? (
                      <>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Booker</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Area</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Discount</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>Packing</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>Std Rate</TableCell>
                        <TableCell sx={{ fontWeight: 700, textAlign: "right" }}>Spl Rate</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((row) => (
                    <TableRow
                      key={row.rowNum}
                      sx={{
                        bgcolor:
                          row.action === "error"
                            ? "error.50"
                            : row.action === "warning"
                            ? "warning.50"
                            : "inherit",
                      }}
                    >
                      <TableCell>{row.rowNum}</TableCell>
                      <TableCell>
                        <ActionChip action={row.action} />
                      </TableCell>
                      {entity === "customers" ? (
                        <>
                          <TableCell>{String(row.data.name ?? "-")}</TableCell>
                          <TableCell>{String(row.data.customer_code || "-")}</TableCell>
                          <TableCell>{String(row.data.booker_name ?? "-")}</TableCell>
                          <TableCell>{String(row.data.area_name ?? "-")}</TableCell>
                          <TableCell>{row.data.discount !== undefined ? `${row.data.discount}%` : "-"}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{String(row.data.name ?? "-")}</TableCell>
                          <TableCell sx={{ textAlign: "right" }}>{String(row.data.packing ?? "-")}</TableCell>
                          <TableCell sx={{ textAlign: "right" }}>
                            {row.data.standard_rate !== undefined ? `Rs ${row.data.standard_rate}` : "-"}
                          </TableCell>
                          <TableCell sx={{ textAlign: "right" }}>
                            {row.data.special_rate !== undefined ? `Rs ${row.data.special_rate}` : "-"}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {errorMsg && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMsg}
              </Alert>
            )}

            {confirming && <LinearProgress sx={{ mt: 2 }} />}
          </Box>
        )}

        {/* ── Step 2: Done ───────────────────────────────────── */}
        {step === 2 && result && (
          <Box textAlign="center" py={3}>
            <CheckIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Import Complete
            </Typography>
            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Chip
                label={`${result.summary.created} created`}
                color="success"
                variant="outlined"
              />
              <Chip
                label={`${result.summary.updated} updated`}
                color="info"
                variant="outlined"
              />
              {result.summary.skipped > 0 && (
                <Chip
                  label={`${result.summary.skipped} skipped`}
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === 0 && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={!canPreview || previewing}
              startIcon={previewing ? <CircularProgress size={16} /> : undefined}
            >
              {previewing ? "Checking..." : "Preview"}
            </Button>
          </>
        )}

        {step === 1 && (
          <>
            <Button onClick={() => { setStep(0); setPreview(null); setErrorMsg(null) }}>
              Back
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleConfirm}
              disabled={validRows.length === 0 || confirming}
              startIcon={confirming ? <CircularProgress size={16} /> : undefined}
            >
              {confirming
                ? "Importing..."
                : `Import ${validRows.length} row${validRows.length !== 1 ? "s" : ""}`}
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <Button onClick={() => { reset() }}>
              Import Another
            </Button>
            <Button variant="contained" onClick={handleClose}>
              Done
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
